import { useTranslation } from 'react-i18next';
import { Gauge, IndianRupee, AlertCircle, Clock } from 'lucide-react';
import type { DashboardStats } from '@/api/types';
import type { DrillDownKind } from './types';
import { StatCard } from './StatCard';

function fmtShort(n: number | null | undefined) {
  const v = isNaN(Number(n)) ? 0 : (Number(n) ?? 0);
  if (v >= 100000) return '₹' + (v / 100000).toFixed(1) + 'L';
  if (v >= 1000) return '₹' + (v / 1000).toFixed(1) + 'k';
  return '₹' + v.toLocaleString('en-IN');
}

interface BusinessHealthRowProps {
  stats: DashboardStats;
  onSelect: (kind: DrillDownKind) => void;
  hoverBorderClassName?: string;
}

export function BusinessHealthRow({ stats, onSelect, hoverBorderClassName }: BusinessHealthRowProps) {
  const { t } = useTranslation();
  const occupancyPct = stats.totalBeds > 0 ? Math.round((stats.occupiedBeds / stats.totalBeds) * 100) : 0;

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      <StatCard
        label={t('dashboard.occupancyRate')}
        value={`${stats.occupiedBeds} / ${stats.totalBeds}`}
        sub={`${occupancyPct}%`}
        icon={<Gauge size={18} />}
        iconBg="bg-teal-50"
        iconColor="text-teal-600"
      />
      <StatCard
        label={t('dashboard.thisMonthRevenue')}
        value={fmtShort(stats.collectedThisMonth)}
        icon={<IndianRupee size={18} />}
        iconBg="bg-purple-50"
        iconColor="text-purple-600"
      />
      <StatCard
        label={t('dashboard.pendingRent')}
        value={fmtShort(stats.pendingAmount)}
        icon={<AlertCircle size={18} />}
        iconBg="bg-amber-50"
        iconColor="text-amber-600"
        onClick={() => onSelect('PENDING_RENT')}
        hoverBorderClassName={hoverBorderClassName}
      />
      <StatCard
        label={t('dashboard.expectedCollectionToday')}
        value={fmtShort(stats.expectedCollectionToday)}
        icon={<Clock size={18} />}
        iconBg="bg-blue-50"
        iconColor="text-blue-600"
        onClick={() => onSelect('EXPECTED_TODAY')}
        hoverBorderClassName={hoverBorderClassName}
      />
    </div>
  );
}
